import { t } from "@lingui/macro";
import React from "react";

import {
  SearchIcon,
  WaitlistIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import {
  VStack,
  HStack,
  Text,
  Pressable,
  Box,
} from "@whoosh/design-system/src/components/primitive";

type TitleProps = {
  leadingText?: string;
  boldText?: string;
  trailingText?: string;
};

export type MobileFiltersBarProps = {
  onPress?: () => void;
  title: TitleProps;
  values: {
    value: string | null;
    placeholder: string;
  }[];
  rightAction?: "Waitlist" | null;
  onRightActionClick?: () => void;
};

export const MobileFiltersBar = ({
  onPress,
  title,
  values,
  rightAction,
  onRightActionClick,
}: MobileFiltersBarProps) => {
  return (
    <HStack
      borderRadius="4"
      borderWidth={1.5}
      borderColor="surface-dividers"
      alignItems="center"
      shadowColor="black"
      shadowOpacity={0.06}
      shadowOffset={{ width: 0, height: 8 }}
      shadowRadius={16}
      minHeight={68}
    >
      <Pressable onPress={onPress} flex={1}>
        <HStack px={5} py={4} gap={4} alignItems="center">
          <SearchIcon width={16} height={16} color="text-primary" />
          <VStack gap={".5"} flex={1}>
            <Title
              leadingText={title.leadingText}
              boldText={title.boldText}
              trailingText={title.trailingText}
            />
            <HStack gap={".5"} alignItems="center" flexWrap="wrap">
              {values.map(({ value, placeholder }, index) => {
                const renderValue = value !== null ? value : placeholder;
                return (
                  <React.Fragment key={index}>
                    {index !== 0 ? (
                      <Text color="text-secondary" variant="mainTiny">
                        ∙
                      </Text>
                    ) : null}
                    <Text
                      variant="mainTiny"
                      color={value !== null ? "text-primary" : "text-secondary"}
                    >
                      {renderValue}
                    </Text>
                  </React.Fragment>
                );
              })}
            </HStack>
          </VStack>
        </HStack>
      </Pressable>
      <RightAction
        rightAction={rightAction}
        onRightActionClick={onRightActionClick}
      />
    </HStack>
  );
};

const RightAction = ({
  rightAction,
  onRightActionClick,
}: {
  rightAction?: "Waitlist" | null;
  onRightActionClick?: () => void;
}) => {
  if (!rightAction) {
    return null;
  }

  return (
    <Box
      borderLeftWidth={1}
      borderLeftColor="surface-dividers"
      paddingLeft={5}
      paddingRight={5}
      height={32}
      justifyContent="center"
    >
      <Pressable
        onPress={onRightActionClick}
        gap={4}
        alignItems="center"
        accessibilityLabel={t`Waitlist`}
      >
        <HStack gap={4}>
          <WaitlistIcon
            width={16}
            height={16}
            color="text-primary"
            accessible={false}
          />
          <VStack gap={".5"} alignItems="flex-start">
            <Text variant="mainBaseMedium" color="text-primary">
              {t`Waitlist`}
            </Text>
          </VStack>
        </HStack>
      </Pressable>
    </Box>
  );
};

const Title = ({ leadingText, boldText, trailingText }: TitleProps) => {
  if (!leadingText && !boldText && !trailingText) {
    return null;
  }

  return (
    <HStack gap={".5"} alignItems="flex-start" flexWrap="wrap">
      {leadingText ? (
        <Text variant="mainBaseMedium" color="text-primary">
          {leadingText}
        </Text>
      ) : null}
      {boldText ? (
        <Text variant="mainBaseSemiBold" color="text-primary">
          {boldText}
        </Text>
      ) : null}
      {trailingText ? (
        <Text variant="mainBaseMedium" color="text-primary">
          {trailingText}
        </Text>
      ) : null}
    </HStack>
  );
};
